/**
 * Admin Invites: create draft invites, send/resend/cancel, list with filters.
 * Admin-only; uses useAdmin() and redirects non-admins.
 */
import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'

const INVITES_QUERY_KEY = ['admin-invites'] as const
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

const TARGET_USER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'broadcaster', label: 'Broadcaster' },
  { value: 'ad_creator', label: 'Ad-Creator' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
]

export interface InviteRow {
  id: string
  email: string
  target_user_type: string
  status: string
  token_hash: string | null
  expires_at: string | null
  created_by: string
  created_at: string
  sent_at: string | null
  accepted_at: string | null
  accepted_user_id: string | null
  cancelled_at: string | null
  notes: string | null
}

export interface InviteAuditEvent {
  id: string
  invite_id: string
  event_type: string
  actor_user_id: string | null
  timestamp: string
  metadata: unknown
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return format(new Date(iso), 'dd MMM yyyy, HH:mm')
  } catch {
    return '—'
  }
}

export default function AdminInvitesPage() {
  const { user, session } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterTargetType, setFilterTargetType] = useState('all')
  const [emailSearch, setEmailSearch] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [detailInviteId, setDetailInviteId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createEmail, setCreateEmail] = useState('')
  const [createTargetType, setCreateTargetType] = useState('broadcaster')
  const [createNotes, setCreateNotes] = useState('')
  const [createExpiryDays, setCreateExpiryDays] = useState(7)
  const [pendingSendId, setPendingSendId] = useState<string | null>(null)
  const [pendingResendId, setPendingResendId] = useState<string | null>(null)
  const [pendingCancelId, setPendingCancelId] = useState<string | null>(null)
  const [editInviteId, setEditInviteId] = useState<string | null>(null)

  const { data: invites = [], isLoading, error } = useQuery({
    queryKey: INVITES_QUERY_KEY,
    queryFn: async (): Promise<InviteRow[]> => {
      const { data, error: err } = await supabase
        .from('invites')
        .select('*')
        .order('created_at', { ascending: false })
      if (err) throw err
      return (data ?? []) as InviteRow[]
    },
    enabled: !!user && isAdmin,
  })

  const filtered = invites.filter((inv) => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false
    if (filterTargetType !== 'all' && inv.target_user_type !== filterTargetType) return false
    if (emailSearch.trim() && !inv.email.toLowerCase().includes(emailSearch.trim().toLowerCase())) return false
    return true
  })

  const createInviteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('create_invite', {
        p_email: createEmail.trim(),
        p_target_user_type: createTargetType,
        p_notes: createNotes.trim() || null,
        p_expiry_days: createExpiryDays,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
      setMessage({ type: 'success', text: 'Invite created. You can send it from the list.' })
      setCreateOpen(false)
      setCreateEmail('')
      setCreateNotes('')
      setCreateTargetType('broadcaster')
      setCreateExpiryDays(7)
      setDetailInviteId(id)
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to create invite' })
    },
  })

  const sendInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const url = `${SUPABASE_URL}/functions/v1/send-invite`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({ inviteId, resend: false }),
      })
      const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; email_sent?: boolean }
      if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`)
      if (!data.success) throw new Error(data?.error ?? 'Send failed')
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
      const emailSent = data?.email_sent === true
      setMessage({
        type: 'success',
        text: emailSent
          ? 'Invite sent. The recipient should receive an email shortly.'
          : 'Invite link created, but no email was sent (RESEND_API_KEY is not set). Share the invite link manually or configure Resend.',
      })
      setPendingSendId(null)
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to send invite' })
      setPendingSendId(null)
    },
  })

  const resendInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const url = `${SUPABASE_URL}/functions/v1/send-invite`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({ inviteId, resend: true }),
      })
      const data = (await res.json().catch(() => ({}))) as { success?: boolean; error?: string; email_sent?: boolean }
      if (!res.ok) throw new Error(data?.error ?? `HTTP ${res.status}`)
      if (!data.success) throw new Error(data?.error ?? 'Resend failed')
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
      const emailSent = data?.email_sent === true
      setMessage({
        type: 'success',
        text: emailSent
          ? 'Invite resent. The recipient should receive an email shortly.'
          : 'Invite link updated, but no email was sent (RESEND_API_KEY is not set).',
      })
      setPendingResendId(null)
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to resend invite' })
      setPendingResendId(null)
    },
  })

  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase.rpc('cancel_invite', { p_invite_id: inviteId })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
      setMessage({ type: 'success', text: 'Invite cancelled.' })
      setPendingCancelId(null)
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to cancel invite' })
      setPendingCancelId(null)
    },
  })

  const updateInviteMutation = useMutation({
    mutationFn: async ({
      inviteId,
      email,
      target_user_type,
      notes,
    }: { inviteId: string; email: string; target_user_type: string; notes: string }) => {
      const { error } = await supabase.rpc('update_invite', {
        p_invite_id: inviteId,
        p_email: email.trim(),
        p_target_user_type: target_user_type,
        p_notes: notes.trim() || null,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITES_QUERY_KEY })
      setMessage({ type: 'success', text: 'Invite updated.' })
      setEditInviteId(null)
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e instanceof Error ? e.message : 'Failed to update invite' })
    },
  })

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        Invites
      </h1>
      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Create and send invites for Broadcaster or Ad-Creator. Invitees use the link in the email to register or log in and receive the role.
      </p>

      {message && (
        <div
          className="mb-4 px-4 py-2 rounded border-2"
          role="alert"
          style={{
            backgroundColor: message.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
            color: 'var(--text-inverse)',
            borderColor: message.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          }}
        >
          {message.text}
        </div>
      )}

      <div
        className="rounded-lg p-6 border-2 mb-6"
        style={{ borderColor: 'var(--bg-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
      >
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <label htmlFor="filter-status" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            Status:
          </label>
          <select
            id="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-xs px-2 py-1 rounded border"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <label htmlFor="filter-target" className="text-sm font-medium ml-2" style={{ color: 'var(--text-primary)' }}>
            Role:
          </label>
          <select
            id="filter-target"
            value={filterTargetType}
            onChange={(e) => setFilterTargetType(e.target.value)}
            className="text-xs px-2 py-1 rounded border"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          >
            <option value="all">All</option>
            {TARGET_USER_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <label htmlFor="email-search" className="text-sm font-medium ml-2" style={{ color: 'var(--text-primary)' }}>
            Email:
          </label>
          <input
            id="email-search"
            type="text"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            placeholder="Search…"
            className="text-xs px-2 py-1 rounded border w-40"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
          <button
            type="button"
            className="ml-4 px-3 py-1.5 rounded border-2 text-sm font-medium"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
            onClick={() => setCreateOpen(true)}
          >
            Create invite
          </button>
        </div>

        {error && (
          <p className="text-sm mb-4" style={{ color: 'var(--color-error)' }} role="alert">
            Failed to load invites. {error instanceof Error ? error.message : 'Unknown error.'}
          </p>
        )}

        {isLoading && <p style={{ color: 'var(--text-secondary)' }}>Loading invites…</p>}

        {!isLoading && !error && filtered.length === 0 && (
          <p style={{ color: 'var(--text-secondary)' }}>No invites match the filter.</p>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" aria-label="Invites">
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Email</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Target role</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Status</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Created</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Sent</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Expires</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Accepted</th>
                  <th className="text-left py-1 px-1.5 text-xs font-medium" style={{ color: 'var(--text-primary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-primary)' }}>{inv.email}</td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {inv.target_user_type === 'broadcaster' ? 'Broadcaster' : 'Ad-Creator'}
                    </td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{inv.status}</td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(inv.created_at)}</td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(inv.sent_at)}</td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(inv.expires_at)}</td>
                    <td className="py-1 px-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>{formatDate(inv.accepted_at)}</td>
                    <td className="py-1 px-1.5">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button"
                          className="text-xs px-1.5 py-0.5 rounded border"
                          style={{
                            borderColor: 'var(--bg-tertiary)',
                            backgroundColor: 'var(--bg-primary)',
                            color: 'var(--text-primary)',
                          }}
                          onClick={() => setDetailInviteId(inv.id)}
                        >
                          View
                        </button>
                        {inv.status === 'draft' && (
                          <>
                            <button
                              type="button"
                              className="text-xs px-1.5 py-0.5 rounded border"
                              style={{
                                borderColor: 'var(--bg-tertiary)',
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                              }}
                              onClick={() => setEditInviteId(inv.id)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              disabled={sendInviteMutation.isPending && pendingSendId === inv.id}
                              className="text-xs px-1.5 py-0.5 rounded border font-medium"
                              style={{
                                borderColor: 'var(--bg-tertiary)',
                                backgroundColor: 'var(--bg-interactive)',
                                color: 'var(--text-inverse)',
                              }}
                              onClick={() => {
                                setPendingSendId(inv.id)
                                sendInviteMutation.mutate(inv.id)
                              }}
                            >
                              Send
                            </button>
                            <button
                              type="button"
                              disabled={cancelInviteMutation.isPending && pendingCancelId === inv.id}
                              className="text-xs px-1.5 py-0.5 rounded border"
                              style={{
                                borderColor: 'var(--color-error)',
                                color: 'var(--color-error)',
                                backgroundColor: 'transparent',
                              }}
                              onClick={() => {
                                if (window.confirm('Cancel this invite?')) {
                                  setPendingCancelId(inv.id)
                                  cancelInviteMutation.mutate(inv.id)
                                }
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {(inv.status === 'sent' || inv.status === 'expired') && (
                          <>
                            <button
                              type="button"
                              disabled={resendInviteMutation.isPending && pendingResendId === inv.id}
                              className="text-xs px-1.5 py-0.5 rounded border"
                              style={{
                                borderColor: 'var(--bg-tertiary)',
                                backgroundColor: 'var(--bg-primary)',
                                color: 'var(--text-primary)',
                              }}
                              onClick={() => {
                                setPendingResendId(inv.id)
                                resendInviteMutation.mutate(inv.id)
                              }}
                            >
                              Resend
                            </button>
                            {inv.status === 'sent' && (
                              <button
                                type="button"
                                disabled={cancelInviteMutation.isPending && pendingCancelId === inv.id}
                                className="text-xs px-1.5 py-0.5 rounded border"
                                style={{
                                  borderColor: 'var(--color-error)',
                                  color: 'var(--color-error)',
                                  backgroundColor: 'transparent',
                                }}
                                onClick={() => {
                                  if (window.confirm('Cancel this invite? The link will stop working.')) {
                                    setPendingCancelId(inv.id)
                                    cancelInviteMutation.mutate(inv.id)
                                  }
                                }}
                              >
                                Cancel
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-invite-title"
          onClick={() => !createInviteMutation.isPending && setCreateOpen(false)}
        >
          <div
            className="rounded-lg border-2 p-6 max-w-md w-full"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="create-invite-title" className="text-xl font-bold mb-4">Create invite</h2>
            <div className="space-y-3">
              <div>
                <label htmlFor="create-email" className="block text-sm font-medium mb-1">Email <span className="text-color-error">*</span></label>
                <input
                  id="create-email"
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  required
                  className="w-full px-2 py-1.5 rounded border"
                  style={{
                    borderColor: 'var(--bg-tertiary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              <div>
                <label htmlFor="create-target" className="block text-sm font-medium mb-1">Target role <span className="text-color-error">*</span></label>
                <select
                  id="create-target"
                  value={createTargetType}
                  onChange={(e) => setCreateTargetType(e.target.value)}
                  className="w-full px-2 py-1.5 rounded border"
                  style={{
                    borderColor: 'var(--bg-tertiary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {TARGET_USER_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="create-notes" className="block text-sm font-medium mb-1">Notes (optional)</label>
                <textarea
                  id="create-notes"
                  value={createNotes}
                  onChange={(e) => setCreateNotes(e.target.value)}
                  rows={2}
                  className="w-full px-2 py-1.5 rounded border"
                  style={{
                    borderColor: 'var(--bg-tertiary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
              <div>
                <label htmlFor="create-expiry" className="block text-sm font-medium mb-1">Link expires (days)</label>
                <input
                  id="create-expiry"
                  type="number"
                  min={1}
                  max={90}
                  value={createExpiryDays}
                  onChange={(e) => setCreateExpiryDays(Number(e.target.value) || 7)}
                  className="w-20 px-2 py-1.5 rounded border"
                  style={{
                    borderColor: 'var(--bg-tertiary)',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={createInviteMutation.isPending || !createEmail.trim()}
                className="px-4 py-2 rounded border-2 font-medium"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-interactive)',
                  color: 'var(--text-inverse)',
                }}
                onClick={() => createInviteMutation.mutate()}
              >
                {createInviteMutation.isPending ? 'Creating…' : 'Create draft'}
              </button>
              <button
                type="button"
                disabled={createInviteMutation.isPending}
                className="px-4 py-2 rounded border-2"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {detailInviteId && (
        <InviteDetailModal
          inviteId={detailInviteId}
          onClose={() => setDetailInviteId(null)}
        />
      )}

      {editInviteId && (
        <EditInviteModal
          invite={filtered.find((inv) => inv.id === editInviteId) ?? invites.find((inv) => inv.id === editInviteId)}
          onClose={() => setEditInviteId(null)}
          onSave={(email, target_user_type, notes) => {
            if (editInviteId) {
              updateInviteMutation.mutate({ inviteId: editInviteId, email, target_user_type, notes })
            }
          }}
          isPending={updateInviteMutation.isPending}
        />
      )}
    </div>
  )
}

function EditInviteModal({
  invite,
  onClose,
  onSave,
  isPending,
}: {
  invite: InviteRow | undefined
  onClose: () => void
  onSave: (email: string, target_user_type: string, notes: string) => void
  isPending: boolean
}) {
  const [email, setEmail] = useState(invite?.email ?? '')
  const [target_user_type, setTargetUserType] = useState(invite?.target_user_type ?? 'broadcaster')
  const [notes, setNotes] = useState(invite?.notes ?? '')

  useEffect(() => {
    if (invite) {
      setEmail(invite.email)
      setTargetUserType(invite.target_user_type)
      setNotes(invite.notes ?? '')
    }
  }, [invite?.id, invite?.email, invite?.target_user_type, invite?.notes])

  if (!invite) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(email, target_user_type, notes)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-invite-title"
      onClick={() => !isPending && onClose()}
    >
      <div
        className="rounded-lg border-2 p-6 max-w-md w-full"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="edit-invite-title" className="text-xl font-bold mb-4">Edit invite (draft)</h2>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="edit-email" className="block text-sm font-medium mb-1">Email <span className="text-color-error">*</span></label>
            <input
              id="edit-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-2 py-1.5 rounded border"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <div>
            <label htmlFor="edit-target" className="block text-sm font-medium mb-1">Target role <span className="text-color-error">*</span></label>
            <select
              id="edit-target"
              value={target_user_type}
              onChange={(e) => setTargetUserType(e.target.value)}
              className="w-full px-2 py-1.5 rounded border"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              {TARGET_USER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-notes" className="block text-sm font-medium mb-1">Notes (optional)</label>
            <textarea
              id="edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-2 py-1.5 rounded border"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={isPending || !email.trim()}
              className="px-4 py-2 rounded border-2 font-medium"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
            >
              {isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={isPending}
              className="px-4 py-2 rounded border-2"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function InviteDetailModal({ inviteId, onClose }: { inviteId: string; onClose: () => void }) {
  const { data: invite } = useQuery({
    queryKey: ['admin-invite-detail', inviteId],
    queryFn: async () => {
      const { data, error } = await supabase.from('invites').select('*').eq('id', inviteId).single()
      if (error) throw error
      return data as InviteRow
    },
    enabled: !!inviteId,
  })
  const { data: events = [] } = useQuery({
    queryKey: ['admin-invite-audit', inviteId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invite_audit_events')
        .select('*')
        .eq('invite_id', inviteId)
        .order('timestamp', { ascending: true })
      if (error) throw error
      return (data ?? []) as InviteAuditEvent[]
    },
    enabled: !!inviteId,
  })

  if (!invite) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-detail-title"
      onClick={onClose}
    >
      <div
        className="rounded-lg border-2 p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="invite-detail-title" className="text-xl font-bold mb-4">Invite details</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm mb-4">
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Email</dt>
          <dd>{invite.email}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Target role</dt>
          <dd>{invite.target_user_type === 'broadcaster' ? 'Broadcaster' : 'Ad-Creator'}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Status</dt>
          <dd>{invite.status}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Created</dt>
          <dd>{formatDate(invite.created_at)}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Sent</dt>
          <dd>{formatDate(invite.sent_at)}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Expires</dt>
          <dd>{formatDate(invite.expires_at)}</dd>
          <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Accepted</dt>
          <dd>{formatDate(invite.accepted_at)}</dd>
          {invite.notes && (
            <>
              <dt className="font-medium" style={{ color: 'var(--text-secondary)' }}>Notes</dt>
              <dd>{invite.notes}</dd>
            </>
          )}
        </dl>
        <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Audit timeline</h3>
        <ul className="text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
          {events.map((ev) => (
            <li key={ev.id}>
              {formatDate(ev.timestamp)} — {ev.event_type}
              {ev.actor_user_id ? ` (actor: ${ev.actor_user_id.slice(0, 8)}…)` : ''}
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mt-4 px-4 py-2 rounded border-2"
          style={{
            borderColor: 'var(--bg-tertiary)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
          }}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  )
}
